import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Gauge, RefreshCw, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { einschaetzungAPI } from '@/lib/api';

/* Lead-Einschaetzung (v5, M1) — Creatio-Layout: Ampel gross, darunter die
   acht Kriterien als Punktreihe, Risiken, dann die Aktions-Karte mit
   "Uebernehmen". Bewertet den Prozessstand, nie die Person. Nur intern. */

const AMPEL = {
  gruen: { cls: 'bg-green-500', label: 'Grün', text: 'Prozess läuft' },
  gelb: { cls: 'bg-amber-400', label: 'Gelb', text: 'Ein Schritt fehlt' },
  rot: { cls: 'bg-red-500', label: 'Rot', text: 'Kontakt abgerissen' },
};
const STATUS = { erfuellt: 'bg-green-500', offen: 'bg-amber-400', unbekannt: 'bg-slate-300' };
const AKTION = { nachricht: 'Nachricht', anruf: 'Anruf', termin: 'Termin', angebot: 'Angebot', warten: 'Warten' };

export default function EinschaetzungPanel({ leadId }) {
  const navigate = useNavigate();
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);
  const [rechnet, setRechnet] = useState(false);
  const [uebernimmt, setUebernimmt] = useState(false);

  const lade = useCallback(async () => {
    try { const r = await einschaetzungAPI.lesen(leadId); setDaten(r.data); }
    catch (e) { /* Panel bleibt leer, Lead-Seite funktioniert trotzdem */ }
    finally { setLaden(false); }
  }, [leadId]);
  useEffect(() => { lade(); }, [lade]);

  const neu = async () => {
    setRechnet(true);
    try {
      const r = await einschaetzungAPI.neu(leadId);
      if (r.data?.fehler) toast.error(`Einschätzung fehlgeschlagen: ${r.data.fehler}`);
      else toast.success('Neu eingeschätzt');
      await lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Einschätzung nicht möglich'); }
    finally { setRechnet(false); }
  };

  const uebernehmen = async () => {
    setUebernimmt(true);
    try {
      const r = await einschaetzungAPI.aktionUebernehmen(leadId);
      if (r.data?.art === 'freigabe') { toast.success('Entwurf liegt in der Freigabe-Zentrale'); navigate('/freigabe', { state: { freigabe_id: r.data.id } }); }
      else toast.success(r.data?.schon ? 'Aktion war schon übernommen' : 'Aufgabe angelegt');
      await lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Aktion konnte nicht übernommen werden'); }
    finally { setUebernimmt(false); }
  };

  const a = daten?.aktuell;
  const krit = daten?.kriterien || [];
  const statusVon = (k) => (a?.kriterien || []).find((x) => x.schluessel === k) || {};
  const ampel = a ? AMPEL[a.ampel] || AMPEL.gelb : null;

  return (
    <Card className="border-border/60" data-testid="einschaetzung-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">Einschätzung</span>
          </div>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={neu} disabled={rechnet} data-testid="einschaetzung-neu">
            {rechnet ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="ml-1">Neu einschätzen</span>
          </Button>
        </div>

        {laden ? (
          <p className="text-xs text-muted-foreground">Lade …</p>
        ) : !a ? (
          <p className="text-xs text-muted-foreground">Noch keine Einschätzung. „Neu einschätzen" fragt die KI — nur intern sichtbar.</p>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <span className={`inline-block h-8 w-8 rounded-full ${ampel.cls}`} data-testid="einschaetzung-ampel" title={ampel.label} />
              <div>
                <p className="text-sm font-semibold">{ampel.label} · Score {a.score}</p>
                <p className="text-[11px] text-muted-foreground">{ampel.text} · {new Date(a.erstellt_am).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })} · {a.ausloeser}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-1">
              {krit.map((k) => {
                const s = statusVon(k.schluessel);
                return (
                  <div key={k.schluessel} className="flex items-start gap-2 text-xs" title={s.begruendung || ''}>
                    <span className={`mt-1 inline-block h-2.5 w-2.5 rounded-full shrink-0 ${STATUS[s.status] || STATUS.unbekannt}`} />
                    <span className="text-foreground">{k.label}</span>
                    {s.begruendung && <span className="text-muted-foreground truncate">— {s.begruendung}</span>}
                  </div>
                );
              })}
            </div>

            {a.risiken?.length > 0 && (
              <ul className="text-xs space-y-0.5">
                {a.risiken.map((r, i) => <li key={i} className="text-red-700 dark:text-red-400">⚠ {r}</li>)}
              </ul>
            )}

            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-1" data-testid="einschaetzung-aktion">
              <p className="text-xs font-semibold">Nächste Aktion: {AKTION[a.naechste_aktion?.typ] || a.naechste_aktion?.typ}
                {a.naechste_aktion?.kanal ? ` · ${a.naechste_aktion.kanal}` : ''}
                {a.naechste_aktion?.faellig_am ? ` · bis ${new Date(a.naechste_aktion.faellig_am).toLocaleDateString('de-DE')}` : ''}</p>
              {a.naechste_aktion?.text && <p className="text-xs whitespace-pre-wrap">{a.naechste_aktion.text}</p>}
              {a.naechste_aktion?.begruendung && <p className="text-[11px] text-muted-foreground">{a.naechste_aktion.begruendung}</p>}
              <Button size="sm" className="h-7 text-xs mt-1" onClick={uebernehmen} disabled={uebernimmt || !!a.aktion_uebernommen} data-testid="einschaetzung-uebernehmen">
                {a.aktion_uebernommen ? 'Übernommen' : 'Übernehmen'} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
